import type { MetaReducer } from '@ngrx/store';
import { NGRX_STORAGE_KEYS } from '../../../../core/store/ngrx-persisted-state.keys';
import { createStorageSyncMetaReducer } from '../../../../core/store/ngrx-storage-sync.meta-reducer';
import { JobsListFeatureState } from './jobs-list.models';
import { jobsListFeature, initialJobsListState } from './jobs-list.reducer';
import { getPaginationPage } from './jobs-list.cache';

function shouldClearFromStorage(state: JobsListFeatureState): boolean {
  const filters = state.filters;
  const filtersDefault =
    (filters.companyName === null || filters.companyName === '') &&
    (filters.jobRole === '' || filters.jobRole === undefined) &&
    filters.cityId === null &&
    (filters.cityFilterLabel === null || filters.cityFilterLabel === '') &&
    filters.stateId === null &&
    filters.sourceId === null &&
    filters.flameLevelId === null &&
    filters.requiredExperience === null &&
    filters.jobPostStartDate === null &&
    filters.jobPostEndDate === null &&
    filters.applyStatus === true &&
    filters.myJob === true;

  const paginationDefault =
    state.pagination.firstRowOffset === 0 &&
    state.pagination.pageSize === initialJobsListState.pagination.pageSize &&
    getPaginationPage(state.pagination) === 1 &&
    state.pagination.totalRecords === 0;

  const sortDefault =
    state.sort.sortField === null &&
    state.sort.sortOrder === 1 &&
    state.sort.previousSortField === '' &&
    state.sort.previousSortOrder === 0;

  const studentDefault =
    state.student.selectedStudentId === null &&
    (state.student.selectedStudentLabel === null || state.student.selectedStudentLabel === '');

  const noGridData = state.gridData === null;

  return (
    filtersDefault &&
    paginationDefault &&
    sortDefault &&
    studentDefault &&
    state.isHotJobHeaderFilter === false &&
    noGridData
  );
}

function parseJobsListState(raw: unknown): JobsListFeatureState | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const partial = raw as Partial<JobsListFeatureState>;
  if (!partial.filters || !partial.pagination || !partial.sort) {
    return null;
  }

  const paginationPartial = partial.pagination as JobsListFeatureState['pagination'];
  const rawGridData = partial.gridData ?? null;
  const legacyTotalRecords =
    rawGridData && typeof rawGridData === 'object' && 'totalRecords' in rawGridData
      ? Number((rawGridData as { totalRecords?: number }).totalRecords) || 0
      : null;
  const normalizedGridData =
    rawGridData && typeof rawGridData === 'object' && Array.isArray(rawGridData.jobs)
      ? { jobs: rawGridData.jobs }
      : null;

  const parsedPagination: JobsListFeatureState['pagination'] = {
    ...initialJobsListState.pagination,
    ...paginationPartial,
    page: paginationPartial.page ?? getPaginationPage({
      ...initialJobsListState.pagination,
      ...paginationPartial
    }),
    totalRecords: paginationPartial.totalRecords ?? legacyTotalRecords ?? 0
  };

  return {
    filters: { ...initialJobsListState.filters, ...partial.filters },
    pagination: parsedPagination,
    sort: partial.sort,
    student:
      partial.student && typeof partial.student === 'object'
        ? partial.student
        : initialJobsListState.student,
    isHotJobHeaderFilter:
      typeof partial.isHotJobHeaderFilter === 'boolean'
        ? partial.isHotJobHeaderFilter
        : false,
    gridData: normalizedGridData
  };
}

export const jobsListMetaReducers: MetaReducer[] = [
  createStorageSyncMetaReducer<JobsListFeatureState>({
    featureKey: jobsListFeature.name,
    storageKey: NGRX_STORAGE_KEYS.jobsList,
    shouldClearFromStorage,
    serialize: (state) => state,
    deserialize: parseJobsListState
  })
];
